import React from 'react';
import { BaseModal } from './BaseModal';
import { ImageZoom } from './ImageZoom';

interface OneTrustSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OneTrustSignupModal({ isOpen, onClose }: OneTrustSignupModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="OneTrust Data Subject Creation"
      size="large"
    >
      <div className="space-y-6 text-gray-600 dark:text-gray-300">
        {/* Section 1: Automated Process */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Automated Data Subject Creation
          </h3>
          <p className="mb-4">
            When you create an account, our system automatically triggers the following process:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>A webhook notification is sent to OneTrust</li>
            <li>OneTrust creates a new data subject profile with your information</li>
            <li>A unique magic link is generated for your preference center</li>
            <li>The magic link is securely stored in your profile</li>
          </ol>
          <div className="mt-6 bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden">
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/ACCOUNT%20CREATION%20OT%20PIC/OT%20GUY%20User%20Account%20Creation%20OT%20webhook.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9BQ0NPVU5UIENSRUFUSU9OIE9UIFBJQy9PVCBHVVkgVXNlciBBY2NvdW50IENyZWF0aW9uIE9UIHdlYmhvb2sucG5nIiwiaWF0IjoxNzQzNjk3MTI1LCJleHAiOjIwNTkwNTcxMjV9.QJD8cbEqHYHtBLf1PJEYZyD7T1OsOsYc8nSMT1J0cno"
              alt="OneTrust Webhook Configuration"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Section 2: Data Subject Profile */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Data Subject Profile Creation
          </h3>
          <p className="mb-4">
            OneTrust automatically creates a comprehensive data subject profile that includes:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Basic contact information</li>
            <li>Communication preferences</li>
            <li>Consent records</li>
            <li>Privacy rights requests</li>
          </ul>
          <div className="mt-6 bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden">
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/ACCOUNT%20CREATION%20OT%20PIC/OT%20GUY%20transaction%20for%20account%20creation.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9BQ0NPVU5UIENSRUFUSU9OIE9UIFBJQy9PVCBHVVkgdHJhbnNhY3Rpb24gZm9yIGFjY291bnQgY3JlYXRpb24ucG5nIiwiaWF0IjoxNzQzNjk3NDM5LCJleHAiOjIwNTkwNTc0Mzl9.IPrgvDYL1__A5_TXuCGtj-azs6OFh_gfS8ZLhQIuJa4"
              alt="OneTrust Data Subject Profile"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Section 3: Preference Center Integration */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Preference Center Integration
          </h3>
          <p className="mb-4">
            Your account is automatically linked to OneTrust's preference center, allowing you to:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Manage communication preferences</li>
            <li>Update privacy settings</li>
            <li>Submit data rights requests</li>
            <li>View consent history</li>
          </ul>
          <div className="mt-6 bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden">
            <ImageZoom 
              src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/ACCOUNT%20CREATION%20OT%20PIC/OT%20GUY%20Preference%20center%20showcase%20for%20account%20creation.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9BQ0NPVU5UIENSRUFUSU9OIE9UIFBJQy9PVCBHVVkgUHJlZmVyZW5jZSBjZW50ZXIgc2hvd2Nhc2UgZm9yIGFjY291bnQgY3JlYXRpb24ucG5nIiwiaWF0IjoxNzQzNjk3MjY0LCJleHAiOjIwNTkwNTcyNjR9.I1hh5pb9VU3aEdOB2p1yc5CUPdY3VNJnpVtDXIN5f7I"
              alt="OneTrust Preference Center Integration"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Your data is handled securely and in compliance with privacy regulations. You can manage your 
            privacy preferences and exercise your data rights at any time through our Privacy Center.
          </p>
        </div>
      </div>
    </BaseModal>
  );
}